import React, { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import frFR from 'antd/locale/fr_FR';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import deDE from 'antd/locale/de_DE';
import ruRU from 'antd/locale/ru_RU';
import itIT from 'antd/locale/it_IT';
import plPL from 'antd/locale/pl_PL';
import nlNL from 'antd/locale/nl_NL';
import svSE from 'antd/locale/sv_SE';
import ptPT from 'antd/locale/pt_PT';
import type { Locale } from 'antd/es/locale';
import { useI18n } from './I18nContext';
import type { LocaleCode } from './types';

const antdLocaleByCode: Record<LocaleCode, Locale> = {
  fr: frFR,
  en: enUS,
  es: esES,
  de: deDE,
  ru: ruRU,
  it: itIT,
  pl: plPL,
  nl: nlNL,
  sv: svSE,
  pt: ptPT,
  no: enUS,
  sq: enUS,
  'de-AT': enUS,
};

const appTheme = {
  token: {
    colorPrimary: '#ffa726',
    borderRadius: 12,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    colorBgContainer: 'rgba(255, 255, 255, 0.03)',
    colorBgElevated: 'rgba(10, 10, 10, 0.95)',
    colorText: '#ffffff',
    colorTextSecondary: 'rgba(255, 255, 255, 0.7)',
    colorBorder: 'rgba(255, 255, 255, 0.1)',
    colorBorderSecondary: 'rgba(255, 255, 255, 0.05)',
  },
  components: {
    Card: {
      colorBgContainer: 'rgba(255, 255, 255, 0.03)',
      colorBorderSecondary: 'rgba(255, 255, 255, 0.1)',
    },
    Input: {
      colorBgContainer: 'rgba(255, 255, 255, 0.05)',
      colorBorder: 'rgba(255, 255, 255, 0.2)',
      colorText: '#ffffff',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.4)',
    },
    Button: {
      colorPrimary: '#ffa726',
      colorPrimaryHover: '#ff9800',
      colorPrimaryActive: '#f57c00',
    },
  },
};

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const { locale } = useI18n();
  const antdLocale = useMemo(() => antdLocaleByCode[locale] || enUS, [locale]);

  return (
    <ConfigProvider locale={antdLocale} theme={appTheme}>
      {children}
    </ConfigProvider>
  );
}
